import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Check, Ban } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatKWD, colorHex } from "@/lib/format";

const KIND_LABEL = { return: "إرجاع", exchange: "استبدال" };
const STATUS = {
  requested: { ar: "بانتظار الموافقة", cls: "text-accent" },
  approved: { ar: "تمت الموافقة", cls: "text-green-700" },
  rejected: { ar: "مرفوض", cls: "text-destructive" },
};

function CreateModal({ onClose, onSaved }) {
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [invId, setInvId] = useState("");
  const [kind, setKind] = useState("return");
  const [reason, setReason] = useState("");
  const [retQ, setRetQ] = useState({}); // key idx -> qty
  const [exRows, setExRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/invoices").then((r) => setInvoices(r.data.filter((i) => i.invoice_no && i.status === "completed")));
    api.get("/admin/products").then((r) => setProducts(r.data.filter((p) => p.status === "active")));
  }, []);

  const inv = invoices.find((i) => i.id === invId);

  const addExRow = () => setExRows([...exRows, { product_id: "", variant: "", quantity: 1 }]);
  const setEx = (idx, k, v) => setExRows(exRows.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));
  const removeEx = (idx) => setExRows(exRows.filter((_, i) => i !== idx));

  const save = async () => {
    if (!inv) { toast.error("اختر الفاتورة"); return; }
    const return_items = inv.items
      .map((it, idx) => ({ it, q: parseInt(retQ[idx] || 0) }))
      .filter((x) => x.q > 0)
      .map((x) => ({ product_id: x.it.product_id, size: x.it.size, color: x.it.color, quantity: x.q }));
    if (return_items.length === 0) { toast.error("حدّد كمية صنف واحد على الأقل للإرجاع"); return; }
    let exchange_items = [];
    if (kind === "exchange") {
      exchange_items = exRows.filter((r) => r.product_id && r.variant).map((r) => {
        const [size, color] = r.variant.split("|");
        return { product_id: r.product_id, size, color, quantity: parseInt(r.quantity || 1) };
      });
      if (exchange_items.length === 0) { toast.error("حدّد صنف الاستبدال"); return; }
    }
    setSaving(true);
    try {
      await api.post("/staff/returns", { invoice_id: invId, kind, reason, return_items, exchange_items });
      toast.success("تم إنشاء الطلب");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-lg my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="return-form">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl">طلب إرجاع / استبدال</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <label className="text-xs text-text-muted">الفاتورة</label>
        <select value={invId} onChange={(e) => { setInvId(e.target.value); setRetQ({}); }} data-testid="return-invoice-select"
          className="w-full border border-border px-3 py-2.5 text-sm mb-4 mt-1">
          <option value="">— اختر فاتورة —</option>
          {invoices.map((i) => <option key={i.id} value={i.id}>#{i.invoice_no} · {formatKWD(i.total)} · {new Date(i.created_at).toLocaleDateString("ar-KW")}</option>)}
        </select>

        <div className="flex gap-2 mb-4">
          {["return", "exchange"].map((k) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`return-kind-${k}`}
              className={`flex-1 py-2 text-sm border ${kind === k ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>

        {inv && (
          <div className="mb-4">
            <p className="text-xs text-text-muted mb-2">الأصناف المرتجعة (كمية):</p>
            {inv.items.map((it, idx) => (
              <div key={idx} className="flex items-center justify-between border border-border px-3 py-2 mb-1.5 text-sm">
                <span>{it.name_ar} · {it.size}/{it.color_ar || it.color} <span className="text-text-muted">(مباع {it.quantity})</span></span>
                <input type="number" min="0" max={it.quantity} value={retQ[idx] || ""} onChange={(e) => setRetQ({ ...retQ, [idx]: e.target.value })}
                  data-testid={`return-qty-${idx}`} placeholder="0" className="w-16 border border-border px-2 py-1 text-sm" />
              </div>
            ))}
          </div>
        )}

        {kind === "exchange" && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-text-muted">أصناف الاستبدال:</p>
              <button onClick={addExRow} data-testid="return-add-exchange" className="flex items-center gap-1 text-xs border border-border px-2 py-1"><Plus className="w-3 h-3" /> إضافة</button>
            </div>
            {exRows.map((r, idx) => {
              const prod = products.find((p) => p.id === r.product_id);
              return (
                <div key={idx} className="flex items-center gap-2 mb-1.5">
                  <select value={r.product_id} onChange={(e) => setEx(idx, "product_id", e.target.value)} className="border border-border px-2 py-1.5 text-sm flex-1">
                    <option value="">منتج</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name_ar}</option>)}
                  </select>
                  <select value={r.variant} onChange={(e) => setEx(idx, "variant", e.target.value)} className="border border-border px-2 py-1.5 text-sm flex-1" disabled={!prod}>
                    <option value="">المقاس/اللون</option>
                    {prod?.variants.map((v, i) => <option key={i} value={`${v.size}|${v.color}`} disabled={v.stock <= 0}>{v.size}/{v.color_ar || v.color} ({v.stock})</option>)}
                  </select>
                  <input type="number" min="1" value={r.quantity} onChange={(e) => setEx(idx, "quantity", e.target.value)} className="w-14 border border-border px-2 py-1.5 text-sm" />
                  <button onClick={() => removeEx(idx)}><X className="w-4 h-4 text-text-muted" /></button>
                </div>
              );
            })}
          </div>
        )}

        <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="سبب الإرجاع/الاستبدال" rows={2} data-testid="return-reason"
          className="w-full border border-border px-3 py-2.5 text-sm mb-4" />
        <button onClick={save} disabled={saving} data-testid="return-submit" className="w-full bg-primary text-primary-foreground py-3 text-sm disabled:opacity-50">
          {saving ? "…" : "إنشاء الطلب"}
        </button>
      </div>
    </div>
  );
}

export default function AdminReturns() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [rejecting, setRejecting] = useState(null); // return being rejected
  const [rejectReason, setRejectReason] = useState("");

  const load = () => api.get("/admin/returns").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    if (!window.confirm(`تأكيد الموافقة على ${KIND_LABEL[r.kind]} للفاتورة #${r.invoice_no}؟`)) return;
    try { await api.put(`/admin/returns/${r.id}/approve`); toast.success("تمت الموافقة"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("يجب إدخال سبب الرفض"); return; }
    try { await api.put(`/admin/returns/${rejecting.id}/reject`, { reason: rejectReason.trim() }); toast.success("تم الرفض"); setRejecting(null); setRejectReason(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reopen = async (r) => {
    if (!window.confirm(`إعادة فتح الطلب المرفوض للفاتورة #${r.invoice_no}؟`)) return;
    try { await api.put(`/admin/returns/${r.id}/reopen`); toast.success("تمت إعادة الفتح"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-5 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl">المرتجعات والاستبدال</h1>
        <button onClick={() => setOpen(true)} data-testid="add-return-btn" className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 text-sm">
          <Plus className="w-4 h-4" /> طلب جديد
        </button>
      </div>

      <div className="space-y-3">
        {rows.length === 0 && <p className="text-text-muted text-sm">لا توجد طلبات إرجاع بعد</p>}
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-border p-4" data-testid={`return-row-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium">{KIND_LABEL[r.kind]}</span>
                <span className="text-text-muted">فاتورة #{r.invoice_no}</span>
                <span className={STATUS[r.status]?.cls} data-testid={`return-status-${r.id}`}>{STATUS[r.status]?.ar}</span>
              </div>
              {r.status === "requested" && (
                <div className="flex gap-2">
                  <button onClick={() => approve(r)} data-testid={`return-approve-${r.id}`} className="flex items-center gap-1 text-xs bg-[#141110] text-white px-3 py-1.5"><Check className="w-3.5 h-3.5" /> موافقة</button>
                  <button onClick={() => { setRejecting(r); setRejectReason(""); }} data-testid={`return-reject-${r.id}`} className="flex items-center gap-1 text-xs border border-destructive text-destructive px-3 py-1.5"><Ban className="w-3.5 h-3.5" /> رفض</button>
                </div>
              )}
              {r.status === "rejected" && (
                <button onClick={() => reopen(r)} data-testid={`return-reopen-${r.id}`} className="text-xs border border-border px-3 py-1.5">إعادة فتح</button>
              )}
            </div>
            <div className="text-xs text-text-muted space-y-0.5">
              {r.return_items.map((it, i) => <div key={i}>↩︎ {it.name_ar} · {it.size}/{it.color_ar || it.color} ×{it.quantity}</div>)}
              {r.exchange_items.map((it, i) => <div key={i}>↪︎ {it.name_ar} · {it.size}/{it.color_ar || it.color} ×{it.quantity}</div>)}
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <span>قيمة الإرجاع: {formatKWD(r.refund_total)}</span>
              {r.kind === "exchange" && <span>قيمة الاستبدال: {formatKWD(r.exchange_total)}</span>}
              {r.kind === "exchange" && <span>الفرق: {formatKWD(r.balance)}</span>}
              {r.reason && <span className="text-text-muted">· {r.reason}</span>}
            </div>
            {r.status === "rejected" && r.reject_reason && (
              <div className="text-xs text-destructive mt-1">سبب الرفض: {r.reject_reason}</div>
            )}
          </div>
        ))}
      </div>

      {open && <CreateModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}

      {rejecting && (
        <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto overscroll-contain" onClick={() => setRejecting(null)}>
          <div className="bg-white w-[calc(100%-2rem)] max-w-sm p-6 mx-auto mt-6 mb-[50vh] md:mt-[15vh]" onClick={(e) => e.stopPropagation()} data-testid="reject-modal">
            <h3 className="font-display text-xl mb-2">رفض الطلب</h3>
            <p className="text-xs text-text-muted mb-3">يجب إدخال سبب الرفض — لا يمكن الرفض بضغطة واحدة.</p>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} data-testid="reject-reason-input"
              placeholder="سبب الرفض" onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)} className="w-full border border-border px-3 py-2.5 text-base mb-3" />
            <div className="flex gap-2">
              <button onClick={() => setRejecting(null)} data-testid="reject-cancel-btn" className="flex-1 border border-border min-h-[44px] py-2.5 text-sm">إلغاء</button>
              <button onClick={doReject} data-testid="reject-confirm-btn" className="flex-1 bg-destructive text-white min-h-[44px] py-2.5 text-sm">تأكيد الرفض</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
